// src/pages/providers/2_dashboard/components/DashboardPersonName.js
//
// The person's name in a dashboard table row, as a second way to open their
// record. On small screens the Actions column is off to the right and the
// name column is pinned, so making the name interactive saves a horizontal
// scroll. Email, avatar and the rest of the cell stay plain text — only the
// name is a control.
//
// Two call shapes, one look:
//   - `to` set  → a react-router Link (patient overview; Cmd-click / new tab work)
//   - `onClick` only → a button (staff edit modal; there is no URL to open)
//
// At rest the name looks exactly like today's table text. Underline and plum
// appear only on hover or keyboard focus. No focus ring: the table body
// scrolls inside overflow-auto and a ring on the sticky name cell would clip.

import React from 'react';
import { Link } from 'react-router-dom';

const NAME_CONTROL_CLASS =
  'block font-semibold text-xs text-slate-800 truncate cursor-pointer ' +
  'hover:underline hover:text-primary ' +
  'focus-visible:underline focus-visible:text-primary';

/**
 * Interactive person name used in clinician dashboard tables.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children Visible name text
 * @param {string} props.ariaLabel Accessible name including the verb
 *   ("View patient overview for Jane Smith" / "Edit details for Dr Test Support")
 * @param {string} [props.to] Overview path. When set, renders a Link.
 * @param {object} [props.state] React Router location state for the Link
 * @param {function} [props.onClick] Analytics and/or modal-open handler.
 *   Must not preventDefault on the Link — the browser still needs to navigate.
 */
export default function DashboardPersonName({
  children,
  ariaLabel,
  to,
  state,
  onClick,
}) {
  // Middle-click opens a link in a new tab but does not fire `click` in
  // Chrome. Record the same analytics as a left click without blocking the
  // browser's default (do not preventDefault).
  const handleAuxClick = (event) => {
    if (event.button === 1 && typeof onClick === 'function') {
      onClick(event);
    }
  };

  // Links activate on Enter natively; Space would otherwise scroll the page.
  // Calling click() reuses the same path as a mouse activation so analytics
  // still fire once.
  const handleLinkKeyDown = (event) => {
    if (event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      event.currentTarget.click();
    }
  };

  if (to) {
    return (
      <Link
        to={to}
        state={state}
        aria-label={ariaLabel}
        className={NAME_CONTROL_CLASS}
        onClick={onClick}
        onAuxClick={handleAuxClick}
        onKeyDown={handleLinkKeyDown}
      >
        {children}
      </Link>
    );
  }

  // Reset button chrome so the staff name still reads as table text, not a
  // filled control sitting in the name cell.
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      className={`${NAME_CONTROL_CLASS} text-left bg-transparent border-0 p-0`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
